import { initializeApp, getApp, getApps } from 'firebase/app'
import { getFirestore } from 'firebase/firestore'
import { FIREBASE_CONFIG } from 'src/config'
import { ALLOWED_FIRESTORE_DATABASE_IDS } from 'src/services/consts/firebase.const'
import { ERROR_TYPES } from 'src/services/consts/api.const'

let app = null

/**
 * Returns the singleton Firebase app, creating it with FIREBASE_CONFIG if needed.
 * Safe to call from multiple places (e.g. version-check boot and FCM); same instance is reused.
 * @returns {import('firebase/app').FirebaseApp}
 * @throws {Error} error.name = 'FirebaseInitError' if initialization fails
 */
export function getFirebaseApp() {
  if (app) return app

  try {
    app = getApps().length ? getApp() : initializeApp(FIREBASE_CONFIG)
    return app
  } catch (err) {
    const error = new Error(`Failed to initialize Firebase: ${err.message}`)
    error.name = ERROR_TYPES.FIREBASE
    throw error
  }
}

/**
 * Returns a Firestore instance for the given database ID.
 * @param {import('firebase/app').FirebaseApp} firebaseApp - Firebase app instance from getFirebaseApp()
 * @param {string} databaseId - Must be one of ALLOWED_FIRESTORE_DATABASE_IDS (see firebase.const.js)
 * @returns {import('firebase/firestore').Firestore}
 * @throws {Error} error.name = 'FirestoreDbError' if databaseId is invalid
 */
export function getFirestoreDb(firebaseApp, databaseId) {
  if (!ALLOWED_FIRESTORE_DATABASE_IDS.includes(databaseId)) {
    const error = new Error(`Invalid Firestore database ID: ${databaseId}`)
    error.name = ERROR_TYPES.FIREBASE
    throw error
  }

  return getFirestore(firebaseApp, databaseId)
}
